import { version as SDK_VERSION } from "../package.json"
import { ConfigFormBuilder } from "./config-form.js"
import { sanitizeModalSize } from "./modal-size.js"
import { sanitizePluginMetadata } from "./plugin-metadata.js"
import type {
	ApiCredentialsErrorCode,
	ApiCredentialsPayload,
	ConfigPayload,
	ContextPayload,
	FullscreenChangedPayload,
	ModalSizeBounds,
	NotifyPayload,
	PluginConfigValues,
	PluginMetadata,
	PluginMode,
	PluginPreferences,
	PreferencesPayload,
	SessionTokenErrorCode,
	SessionTokenPayload,
} from "./protocol.js"
import {
	MODAL_SIZE_MAX_PX,
	MSG_API_CREDENTIALS,
	MSG_CONFIG,
	MSG_CONFIG_FORM,
	MSG_CONTEXT,
	MSG_FULLSCREEN_CHANGED,
	MSG_MODAL_SIZE,
	MSG_MODE,
	MSG_NOTIFY,
	MSG_PLUGIN_METADATA,
	MSG_PREFERENCES,
	MSG_READY,
	MSG_REQUEST_API_CREDENTIALS,
	MSG_REQUEST_CONFIG_FORM,
	MSG_REQUEST_SESSION_TOKEN,
	MSG_SESSION_TOKEN,
	NOTIFY_MAX_BODY_LENGTH,
	NOTIFY_MAX_TITLE_LENGTH,
	NOTIFY_VARIANTS,
	PLUGIN_METADATA_MAX_PX,
	PLUGIN_OBJECT_TYPES,
	PROTOCOL_VERSION,
	type ReadyPayload,
} from "./protocol.js"

/** Credentials as the plugin uses them: the host's triple plus where to send it. */
export type ResolvedCredentials = {
	apiKey: string
	apiSha: string
	apiSession: string
	apiBaseUrl: string
}

export type LsPluginErrorCode =
	| ApiCredentialsErrorCode
	| SessionTokenErrorCode
	| "http_error"
	| "invalid_response"
	| "network_error"
	| "timeout"
	// Asked for something only `view` mode has — credentials or a token — in
	// `preview` or `config`, where no host answers. Refused at once rather than
	// left to time out.
	| "unavailable_in_mode"

export class LsPluginApiError extends Error {
	readonly name = "LsPluginApiError"
	readonly code: LsPluginErrorCode
	readonly status?: number

	constructor(
		code: LsPluginErrorCode,
		message?: string,
		status?: number,
		options?: { cause?: unknown },
	) {
		super(message ?? `Livespace API call failed: ${code}`, options)
		this.code = code
		this.status = status
	}
}

declare global {
	/**
	 * Your plugin's config, to type `LsPlugin.config` and the `config` and `ready` events. Empty
	 * by default, which leaves config the open `PluginConfigValues`. Augment it with your fields:
	 *
	 *   declare global {
	 *     interface LsPluginConfig {
	 *       region?: "eu" | "us"
	 *     }
	 *   }
	 *
	 * The shape is your claim, not a guarantee: Livespace delivers the config as it was stored, so
	 * a key can be missing, or left over from an older form. Keep the properties optional.
	 */
	interface LsPluginConfig {}
}

/** The config's type: your {@link LsPluginConfig} once you augment it, the open record until then. */
export type PluginConfig = keyof LsPluginConfig extends never
	? PluginConfigValues
	: LsPluginConfig

/**
 * What a plugin needs to render, as one value.
 *
 * `context` is null in `preview` mode. `config` is null when the plugin has
 * never been configured — which is not the same as `{}`.
 */
export type ReadyResult = {
	context: ContextPayload | null
	config: PluginConfig | null
}

/** What {@link LsPluginClient.on} can be subscribed to, and what each delivers. */
export type PluginEventMap = {
	mode: PluginMode
	ready: ReadyResult
	context: ContextPayload
	config: PluginConfig | null
	fullscreen: boolean
	preferences: PluginPreferences | null
}

type ApiParamValue = string | number | boolean

export type GetTokenOptions = {
	/**
	 * Fetch a new token even if the cached one looks fresh. For when your
	 * backend has just answered 401 — `backend.fetch` does this for you.
	 */
	forceRefresh?: boolean
}

/**
 * How long before expiry a cached token is replaced: D2 of "Livespace Plugin
 * Authorization". Capped at half the token's lifetime, so a very short-lived
 * token is still reused at all.
 */
const TOKEN_REFRESH_MARGIN_MS = 15_000

type CachedToken = { token: string; refreshAt: number }

export type LsPluginClientOptions = {
	/** The window to post to. Defaults to `window.parent`. */
	parentWindow?: Window
	/** The window to listen on. Defaults to `window`. */
	listenTo?: Window
	/** The Livespace origin. Resolved from the embedding chain when omitted. */
	hostOrigin?: string
	fetchImpl?: typeof fetch
	randomId?: () => string
	delay?: (ms: number) => Promise<void>
	/** How long to wait for a credential reply. `0` disables the timeout. */
	timeoutMs?: number
	/** How long to wait for a late config-form declaration before answering empty. */
	configFormGraceMs?: number
	/** The clock the token cache reads. Defaults to `Date.now`. */
	now?: () => number
}

/**
 * Resolves the origin of the page embedding us.
 *
 * `ancestorOrigins` is the reliable answer but is Chromium/WebKit only;
 * `document.referrer` is the Firefox fallback. If neither is available we have
 * no way to validate the host, so we say so loudly rather than silently
 * accepting messages from anywhere — the author can pass `hostOrigin`.
 */
const resolveHostOrigin = (): string | null => {
	if (typeof window === "undefined") {
		return null
	}
	const ancestors = window.location.ancestorOrigins
	if (ancestors && ancestors.length > 0) {
		return ancestors[0]
	}
	if (document.referrer) {
		try {
			return new URL(document.referrer).origin
		} catch {
			/* fall through */
		}
	}
	return null
}

const randomIdFallback = (): string => {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID()
	}
	return `ls-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** A promise whose resolver is callable from elsewhere. */
const deferred = <T>() => {
	let resolve!: (value: T) => void
	let reject!: (reason: unknown) => void
	const promise = new Promise<T>((res, rej) => {
		resolve = res
		reject = rej
	})
	return { promise, resolve, reject }
}

export class LsPluginClient {
	private readonly parentWindow: Window | undefined
	private readonly hostOrigin: string | null
	private readonly fetchImpl: typeof fetch
	private readonly randomId: () => string
	private readonly delay: (ms: number) => Promise<void>
	private readonly timeoutMs: number
	private readonly configFormGraceMs: number
	private readonly now: () => number

	private modeValue: PluginMode | null = null

	private contextValue: ContextPayload | null = null
	private contextReceived = false

	// Config is tracked separately from context because it OUTLIVES the initial
	// handshake: the admin screen re-sends it on every edit so a preview reflects
	// what is being typed. `configReceived` distinguishes "not delivered yet" from
	// "delivered as null", which is what lets `ready()` know it can settle.
	private configValue: PluginConfig | null = null
	private configReceived = false
	// Non-null once `ready` has fired. Doubles as the replay value for a late
	// subscriber and as the "already settled" flag.
	private readyValue: ReadyResult | null = null

	private readonly listeners: {
		[K in keyof PluginEventMap]: Set<(payload: PluginEventMap[K]) => void>
	} = {
		mode: new Set(),
		ready: new Set(),
		context: new Set(),
		config: new Set(),
		fullscreen: new Set(),
		preferences: new Set(),
	}

	// Like config: they can arrive in every mode, and `undefined` (not yet) is a
	// different answer from `null` (the host has none). The JSON is what tells a
	// resend apart from a change.
	private preferencesValue: PluginPreferences | null = null
	private preferencesReceived = false
	private preferencesJson: string | null = null

	// Defaults false rather than null: an embed that is never told otherwise is
	// not fullscreen, so render-time branching needs no "not known yet" case.
	private fullscreenValue = false

	private readonly pendingCredentials = new Map<
		string,
		{ resolve: (c: ResolvedCredentials) => void; reject: (e: unknown) => void }
	>()

	private readonly pendingTokens = new Map<
		string,
		{
			resolve: (t: { token: string; expiresIn: number }) => void
			reject: (e: unknown) => void
		}
	>()
	private cachedToken: CachedToken | null = null
	// Every caller while a request is out gets that request's answer — a forced
	// refresh included, since what is in flight is already fresh.
	private tokenInFlight: Promise<string> | null = null

	private configFormBuilder: ConfigFormBuilder | null = null
	private configFormRequested = false
	private configFormAnswered = false
	private configFormGraceTimer: ReturnType<typeof setTimeout> | null = null
	// P2-21. What `declareMetadata` was given, sanitised; `null` until it is
	// called, which is "nothing to send" rather than "unconstrained".
	private metadata: PluginMetadata | null = null

	constructor(options: LsPluginClientOptions = {}) {
		this.parentWindow =
			options.parentWindow ??
			(typeof window !== "undefined" ? window.parent : undefined)
		this.hostOrigin = options.hostOrigin ?? resolveHostOrigin()
		this.fetchImpl =
			options.fetchImpl ??
			((...args: Parameters<typeof fetch>) => fetch(...args))
		this.randomId = options.randomId ?? randomIdFallback
		this.delay =
			options.delay ??
			((ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
		this.timeoutMs = options.timeoutMs ?? 15_000
		this.configFormGraceMs = options.configFormGraceMs ?? 3_000
		this.now = options.now ?? Date.now

		const listenTo =
			options.listenTo ?? (typeof window !== "undefined" ? window : undefined)

		// Attached BEFORE `ready` is sent, and before any author code runs. The
		// host sends nothing until `ready`, then answers at once — a message posted
		// to a window with no listener is dropped silently and never resent. Sent
		// from here, during the script tag, `ready` also always beats the document's
		// `load`; a document that loads without having said it is taken for an old
		// ready-less plugin (see READY_GRACE_MS).
		listenTo?.addEventListener("message", this.handleMessage as EventListener)

		// Stating the protocol is what gets this plugin the current payload shapes;
		// a `ready` without it is treated as protocol 1 and sent the old ones.
		const ready: ReadyPayload = {
			protocolVersion: PROTOCOL_VERSION,
			sdkVersion: SDK_VERSION,
		}
		this.post({ type: MSG_READY, payload: ready })
	}

	/* ---------------------------------------------------------------- */
	/* Messaging                                                        */
	/* ---------------------------------------------------------------- */

	private post(message: object): void {
		if (!this.parentWindow) {
			return
		}
		if (!this.hostOrigin) {
			// Without a resolved origin we would have to post to "*", handing the
			// message to whatever page embedded us. Refuse instead.
			// biome-ignore lint/suspicious/noConsole: author-facing misconfiguration
			console.error(
				'[LsPlugin] cannot determine the Livespace origin, so no message was sent. Name it on the script tag: <script src="…/ls-plugin.js" data-host-origin="https://app.livespace.io">.',
			)
			return
		}
		this.parentWindow.postMessage(message, this.hostOrigin)
	}

	private handleMessage = (event: MessageEvent): void => {
		// The contract asks plugins to validate the host origin, and the reference
		// plugin authors copy from historically did not. Doing it here means every
		// SDK-based plugin gets it for free.
		if (!this.hostOrigin || event.origin !== this.hostOrigin) {
			return
		}
		const data = event.data as { type?: string; payload?: unknown } | null
		if (!data || typeof data.type !== "string") {
			return
		}

		switch (data.type) {
			case MSG_MODE:
				this.onMode(data.payload as { mode?: PluginMode })
				break
			case MSG_CONTEXT:
				this.onContext(data.payload as ContextPayload)
				break
			case MSG_CONFIG:
				this.onConfig(data.payload as ConfigPayload)
				break
			case MSG_API_CREDENTIALS:
				this.onCredentials(data.payload as ApiCredentialsPayload)
				break
			case MSG_SESSION_TOKEN:
				this.onSessionToken(data.payload as SessionTokenPayload)
				break
			case MSG_REQUEST_CONFIG_FORM:
				this.onConfigFormRequest()
				break
			case MSG_FULLSCREEN_CHANGED:
				this.onFullscreenChanged(data.payload as FullscreenChangedPayload)
				break
			case MSG_PREFERENCES:
				this.onPreferences(data.payload as PreferencesPayload)
				break
			default:
				break
		}
	}

	private onMode(payload: { mode?: PluginMode } | undefined): void {
		const mode = payload?.mode
		if (mode !== "view" && mode !== "config" && mode !== "preview") {
			return
		}
		this.modeValue = mode
		this.emit("mode", mode)
		this.settleReadyIfComplete()
	}

	/**
	 * The host drives this both ways — it can put the embed into fullscreen, and
	 * can pull it back out (Escape, or focus leaving the frame). So this is not
	 * an acknowledgement of anything the plugin asked for; it is the host stating
	 * the current display mode.
	 *
	 * A `rejected` reason only ever accompanies `active: false` for plugins still
	 * using the deprecated request messages. It is ignored here: to a plugin that
	 * never asks, a refusal and "not fullscreen" are the same fact.
	 */
	private onFullscreenChanged(
		payload: FullscreenChangedPayload | undefined,
	): void {
		if (!payload || typeof payload.active !== "boolean") {
			return
		}
		if (payload.active === this.fullscreenValue) {
			return
		}
		this.fullscreenValue = payload.active
		this.emit("fullscreen", payload.active)
	}

	/**
	 * Every context message, not just the first. The host re-sends whenever the
	 * object on the page changes — someone edits the deal — so, like config, this
	 * is a stream rather than a one-off.
	 */
	private onContext(payload: ContextPayload | undefined): void {
		if (!payload || typeof payload !== "object") {
			return
		}
		this.contextValue = payload
		this.contextReceived = true
		this.settleReadyIfComplete()
		this.emit("context", payload)
	}

	/**
	 * Every config message, not just the first. The host re-sends on every edit,
	 * which is the whole point of it being its own message.
	 */
	private onConfig(payload: ConfigPayload | undefined): void {
		if (!payload || typeof payload !== "object" || !("config" in payload)) {
			return
		}
		// The author's declared shape is taken on trust: see LsPluginConfig.
		this.configValue = (payload.config ?? null) as PluginConfig | null
		this.configReceived = true
		this.settleReadyIfComplete()
		this.emit("config", this.configValue)
	}

	/**
	 * The viewer's preferences (P2-33). Not part of `ready`: older hosts never
	 * send them, so nothing may wait for them. Fires only on a real change — the
	 * host sends them again after this page navigates, which is not one.
	 */
	private onPreferences(payload: PreferencesPayload | undefined): void {
		if (
			!payload ||
			typeof payload !== "object" ||
			!("preferences" in payload)
		) {
			return
		}
		const { preferences } = payload
		if (
			preferences !== null &&
			(typeof preferences !== "object" || Array.isArray(preferences))
		) {
			return
		}
		const json = JSON.stringify(preferences)
		if (this.preferencesReceived && json === this.preferencesJson) {
			return
		}
		this.preferencesValue = preferences
		this.preferencesReceived = true
		this.preferencesJson = json
		this.emit("preferences", preferences)
	}

	/**
	 * `ready()` settles once the host has sent everything it is going to for this
	 * mode — which differs by mode, hence the wait for `mode` first:
	 *
	 *   view     context + config
	 *   preview  config only; there is no object to be about, so context is null
	 *   config   never. The plugin is being asked to describe its form, not to
	 *            render; it should branch on `mode()` instead of awaiting this.
	 */
	private settleReadyIfComplete(): void {
		if (this.readyValue !== null || this.modeValue === null) {
			return
		}
		if (this.modeValue === "config") {
			return
		}
		if (this.modeValue === "view" && !this.contextReceived) {
			return
		}
		if (!this.configReceived) {
			return
		}

		this.readyValue = {
			context: this.contextValue,
			config: this.configValue,
		}
		this.emit("ready", this.readyValue)
	}

	private onCredentials(payload: ApiCredentialsPayload | undefined): void {
		if (!payload || typeof payload.requestId !== "string") {
			return
		}
		const { requestId } = payload
		const pending = this.pendingCredentials.get(requestId)
		if (!pending) {
			// Not ours — a stale reply, or another SDK instance on the page.
			return
		}
		this.pendingCredentials.delete(requestId)

		if ("error" in payload) {
			pending.reject(new LsPluginApiError(payload.error))
			return
		}
		pending.resolve({
			apiKey: payload.apiKey,
			apiSha: payload.apiSha,
			apiSession: payload.apiSession,
			apiBaseUrl: payload.apiBaseUrl,
		})
	}

	private onSessionToken(payload: SessionTokenPayload | undefined): void {
		if (!payload || typeof payload.requestId !== "string") {
			return
		}
		const pending = this.pendingTokens.get(payload.requestId)
		if (!pending) {
			// Not ours — a stale reply, or another SDK instance on the page.
			return
		}
		this.pendingTokens.delete(payload.requestId)

		if ("error" in payload) {
			pending.reject(new LsPluginApiError(payload.error))
			return
		}
		const { token, expiresIn } = payload
		if (
			typeof token !== "string" ||
			token.length === 0 ||
			typeof expiresIn !== "number" ||
			!Number.isFinite(expiresIn) ||
			expiresIn <= 0
		) {
			pending.reject(
				new LsPluginApiError(
					"invalid_response",
					"Livespace answered the token request without a usable token.",
				),
			)
			return
		}
		pending.resolve({ token, expiresIn })
	}

	/**
	 * Credentials and tokens exist only in `view` mode; elsewhere no host
	 * answers. A mode not yet known is let through — the host decides.
	 */
	private refusalOutsideView(what: string): LsPluginApiError | null {
		if (this.modeValue === null || this.modeValue === "view") {
			return null
		}
		return new LsPluginApiError(
			"unavailable_in_mode",
			`Livespace issues ${what} only in view mode, not in ${this.modeValue}. In preview, render a mock view instead.`,
		)
	}

	/* ---------------------------------------------------------------- */
	/* Public: lifecycle                                                */
	/* ---------------------------------------------------------------- */

	/**
	 * The configuration as it currently stands, for code that runs after delivery
	 * and just wants the value — a click handler, say.
	 *
	 * THREE states, and the difference matters:
	 *
	 *   undefined  the host has not sent one yet
	 *   null       delivered, and this plugin has never been configured
	 *   object     delivered
	 *
	 * Collapsing the first two would make `config === null` mean both
	 * "unconfigured" and "too early", so a plugin would flash an unconfigured
	 * state during a normal startup. Use {@link on}(`"config"`) to react to
	 * changes; this is only a snapshot.
	 */
	get config(): PluginConfig | null | undefined {
		return this.configReceived ? this.configValue : undefined
	}

	/**
	 * The object being viewed and the viewer — the LATEST context, not the one
	 * `ready` delivered, since the host re-sends it when the object changes.
	 *
	 *   undefined  not known yet — or `config` mode, where nothing is rendered
	 *              and no context is ever sent
	 *   null       settled, and there is no object: `preview` mode
	 *   object     delivered (`view` mode) — from the first context message, so
	 *              it is already set inside the first `context` event, even
	 *              though `ready` still waits for config
	 *
	 * `null` is tied to `ready`, so it always means "definitely no object" rather
	 * than "not arrived yet". Use {@link on}(`"context"`) to react to changes;
	 * this is only a snapshot.
	 */
	get context(): ContextPayload | null | undefined {
		if (this.contextReceived) {
			return this.contextValue
		}
		return this.readyValue === null ? undefined : this.readyValue.context
	}

	/**
	 * Whether the embed is currently fullscreen, for code that branches while
	 * rendering — showing a denser layout inline and a fuller one expanded.
	 *
	 * Unlike {@link config} and {@link context} this has no "not yet" state: an
	 * embed nobody has said anything about is not fullscreen, and `false` is the
	 * correct answer from the first frame. Use {@link on}(`"fullscreen"`) to
	 * re-render when it changes; this is only a snapshot.
	 *
	 * Fullscreen is entered from the Livespace chrome. A plugin cannot request it
	 * — the messages that once allowed that are deprecated and the SDK wraps
	 * neither.
	 */
	get isFullscreen(): boolean {
		return this.fullscreenValue
	}

	/**
	 * How the viewer reads dates, times and amounts in Livespace: their
	 * language, time zone and formats. Use them to format the way Livespace does
	 * beside you. In every mode, including `preview` and `config`.
	 *
	 *   undefined  not received (yet) — an older Livespace never sends them
	 *   null       Livespace has none to give: use your own defaults
	 *   object     delivered; any field inside may be `null` on its own
	 *
	 * Use {@link on}(`"preferences"`) to react to them; this is only a snapshot.
	 */
	get preferences(): PluginPreferences | null | undefined {
		return this.preferencesReceived ? this.preferencesValue : undefined
	}

	/**
	 * Subscribe to what the host tells this plugin.
	 *
	 *   mode    once, as soon as the host says why this embed exists.
	 *   ready   once, when everything this mode delivers has arrived:
	 *           `context` + `config` in `view`, `config` alone in `preview`
	 *           (there is no object to be about, so `context` is null). Never in
	 *           `config` mode — nothing is rendered there.
	 *   context the object being viewed and the viewer, and again whenever the
	 *           object changes. `view` mode only: in `preview` and `config`
	 *           there is no object, so it never fires.
	 *   config  the stored configuration, and again on every change. In `preview`
	 *           the admin screen re-sends as the form is filled in, which is what
	 *           lets a preview follow along.
	 *   fullscreen
	 *           whether the embed is fullscreen, on every change. Entering and
	 *           leaving are both driven by the Livespace chrome.
	 *   preferences
	 *           the viewer's language, time zone and formats, once known and
	 *           again on a real change. Every mode. Not part of `ready`.
	 *
	 * REPLAYING: subscribing late is safe. If the event has already happened, the
	 * listener is called immediately with the value. That matters because the host
	 * can deliver before author code runs — the same race the load-time listener
	 * exists to close — so a plugin must never lose an event by being slow to ask
	 * for it.
	 *
	 * @returns an unsubscribe function.
	 */
	on<K extends keyof PluginEventMap>(
		event: K,
		listener: (payload: PluginEventMap[K]) => void,
	): () => void {
		const listeners = this.listeners[event] as Set<(payload: unknown) => void>
		listeners.add(listener as (payload: unknown) => void)

		// Replay whatever already happened, so order of subscription never matters.
		if (event === "mode" && this.modeValue !== null) {
			;(listener as (p: PluginMode) => void)(this.modeValue)
		}
		if (event === "ready" && this.readyValue !== null) {
			;(listener as (p: ReadyResult) => void)(this.readyValue)
		}
		if (event === "context" && this.contextReceived && this.contextValue) {
			;(listener as (p: ContextPayload) => void)(this.contextValue)
		}
		if (event === "config" && this.configReceived) {
			;(listener as (p: PluginConfig | null) => void)(this.configValue)
		}
		// Replayed only when true, which is the one case where something actually
		// happened before the listener existed. Replaying `false` would fire a
		// change that never occurred and make every subscriber run its exit path
		// on subscribe.
		if (event === "fullscreen" && this.fullscreenValue) {
			;(listener as (p: boolean) => void)(true)
		}
		if (event === "preferences" && this.preferencesReceived) {
			;(listener as (p: PluginPreferences | null) => void)(
				this.preferencesValue,
			)
		}

		return () => {
			listeners.delete(listener as (payload: unknown) => void)
		}
	}

	private emit<K extends keyof PluginEventMap>(
		event: K,
		payload: PluginEventMap[K],
	): void {
		for (const listener of this.listeners[event]) {
			;(listener as (p: PluginEventMap[K]) => void)(payload)
		}
	}

	/* ---------------------------------------------------------------- */
	/* Public: config form                                              */
	/* ---------------------------------------------------------------- */

	/**
	 * Declare the configuration form Livespace should render for this plugin.
	 * Call it once, in the plugin's global scope.
	 */
	createConfigForm(): ConfigFormBuilder {
		const builder = new ConfigFormBuilder()
		this.configFormBuilder = builder
		// The author is mid-chain right now (`.addTextInput(...).addSelect(...)`),
		// so the builder is still empty. Answer on the microtask queue, once the
		// whole chain has run.
		queueMicrotask(() => this.answerConfigFormIfPossible())
		return builder
	}

	private onConfigFormRequest(): void {
		this.configFormRequested = true
		this.answerConfigFormIfPossible()
		if (this.configFormAnswered || this.configFormGraceTimer) {
			return
		}
		// Nothing declared yet. It may still be coming (a schema fetched from the
		// author's own backend), so give it a moment — then answer with an empty
		// form, which tells the host "no config", as distinct from never replying.
		this.configFormGraceTimer = setTimeout(() => {
			this.configFormGraceTimer = null
			void this.onConfigFormGraceOver()
		}, this.configFormGraceMs)
	}

	/**
	 * A host from before P2-33 never sends preferences, so a plugin that declares
	 * its form once it knows the language would never declare it. Tell it the
	 * host has none — `null`, as a host may say itself — and let it fall back,
	 * then answer with whatever it declared.
	 */
	private async onConfigFormGraceOver(): Promise<void> {
		if (!this.configFormAnswered && !this.preferencesReceived) {
			this.onPreferences({ preferences: null })
			// `createConfigForm()` answers on a microtask; let it go first.
			await Promise.resolve()
		}
		await this.flushConfigForm()
	}

	private answerConfigFormIfPossible(): void {
		if (
			!this.configFormRequested ||
			this.configFormAnswered ||
			!this.configFormBuilder
		) {
			return
		}
		this.sendConfigForm(this.configFormBuilder.build().fields)
	}

	private sendConfigForm(
		fields: ReturnType<ConfigFormBuilder["build"]>["fields"],
	): void {
		this.configFormAnswered = true
		if (this.configFormGraceTimer) {
			clearTimeout(this.configFormGraceTimer)
			this.configFormGraceTimer = null
		}
		// Before the form, so a host that settles on the form's arrival already
		// holds the plugin's constraints by then.
		if (this.metadata) {
			this.post({ type: MSG_PLUGIN_METADATA, payload: this.metadata })
		}
		this.post({ type: MSG_CONFIG_FORM, payload: { fields } })
	}

	/**
	 * Answer the pending config-form request now, with whatever is declared —
	 * an empty form if nothing is. Called automatically after the grace period;
	 * exposed so an author who knows they have no config can answer instantly.
	 */
	async flushConfigForm(): Promise<void> {
		if (!this.configFormRequested || this.configFormAnswered) {
			return
		}
		this.sendConfigForm(this.configFormBuilder?.build().fields ?? [])
	}

	/* ---------------------------------------------------------------- */
	/* Public: notifications                                            */
	/* ---------------------------------------------------------------- */

	/**
	 * Show a toast in the Livespace chrome. Fire-and-forget: the host validates,
	 * clamps and rate-limits, so a rejected notification is silent by design.
	 */
	notify(payload: NotifyPayload): void {
		const title = typeof payload?.title === "string" ? payload.title.trim() : ""
		if (title.length === 0) {
			// biome-ignore lint/suspicious/noConsole: author-facing misuse
			console.warn("[LsPlugin] notify() needs a non-empty title.")
			return
		}
		if (
			payload.variant !== undefined &&
			!NOTIFY_VARIANTS.includes(payload.variant)
		) {
			// biome-ignore lint/suspicious/noConsole: author-facing misuse
			console.warn(
				`[LsPlugin] notify() variant must be one of ${NOTIFY_VARIANTS.join(", ")}.`,
			)
			return
		}

		const message: NotifyPayload = {
			title: title.slice(0, NOTIFY_MAX_TITLE_LENGTH),
		}
		if (typeof payload.body === "string" && payload.body.length > 0) {
			message.body = payload.body.slice(0, NOTIFY_MAX_BODY_LENGTH)
		}
		if (payload.variant !== undefined) {
			message.variant = payload.variant
		}

		this.post({ type: MSG_NOTIFY, payload: message })
	}

	/* ---------------------------------------------------------------- */
	/* Public: placement metadata                                       */
	/* ---------------------------------------------------------------- */

	/**
	 * Tell Livespace what this plugin needs to be placed: where it may go, and
	 * the least room it is usable in. Call it once, in the plugin's global
	 * scope, as you would `createConfigForm()`.
	 *
	 * The admin screen uses it to shape what it offers: slots too narrow are
	 * disabled, heights below `minHeightPx` are not offered, and — for a plugin
	 * an admin added by URL — only the listed context types can be chosen. A
	 * plugin installed from the Catalog goes where its Catalog entry says.
	 *
	 * Optional: declaring nothing means nothing constrains you. Unusable values
	 * are dropped with a warning; an older host ignores the call entirely.
	 */
	declareMetadata(metadata: PluginMetadata): void {
		const { metadata: usable, dropped } = sanitizePluginMetadata(metadata)
		if (
			metadata &&
			"allowedObjectTypes" in metadata &&
			!("supportedContextTypes" in metadata)
		) {
			// biome-ignore lint/suspicious/noConsole: author-facing deprecation
			console.warn(
				"[LsPlugin] declareMetadata(): allowedObjectTypes is deprecated, and read as supportedContextTypes. Rename it.",
			)
		}
		const types = dropped.filter(
			(key) => key === "supportedContextTypes" || key === "allowedObjectTypes",
		)
		if (types.length > 0) {
			// An error, not a warning: an empty list is not "anywhere", and the
			// plugin is now treated as declaring no requirements at all.
			// biome-ignore lint/suspicious/noConsole: author-facing misuse
			console.error(
				`[LsPlugin] declareMetadata() refused ${types[0]}: it must be null (no requirements) or a non-empty list of ${PLUGIN_OBJECT_TYPES.join(", ")}.`,
			)
		}
		const dimensions = dropped.filter((key) => !types.includes(key))
		if (dimensions.length > 0) {
			// biome-ignore lint/suspicious/noConsole: author-facing misuse
			console.warn(
				`[LsPlugin] declareMetadata() ignored ${dimensions.join(", ")}: dimensions must be numbers from 1 to ${PLUGIN_METADATA_MAX_PX}.`,
			)
		}
		this.metadata = usable
		// Already answered: the host is listening now, so say it now.
		if (this.configFormAnswered) {
			this.post({ type: MSG_PLUGIN_METADATA, payload: usable })
		}
	}

	/* ---------------------------------------------------------------- */
	/* Public: modal size                                               */
	/* ---------------------------------------------------------------- */

	/**
	 * Tell the host what size bounds your modal should respect, if an admin has
	 * allowed the modal layout for this plugin.
	 *
	 * A hint, not a command: the host starts from its own default, clamps it to
	 * these bounds, then to the space available — a minimum larger than the
	 * screen still gets a modal that fits. Call again to change them, or with
	 * `{}` to return to the default. Unusable values are dropped with a warning;
	 * an older host ignores the call entirely.
	 */
	setModalSize(bounds: ModalSizeBounds): void {
		const { bounds: usable, dropped } = sanitizeModalSize(bounds)
		if (dropped.length > 0) {
			// biome-ignore lint/suspicious/noConsole: author-facing misuse
			console.warn(
				`[LsPlugin] setModalSize() ignored ${dropped.join(", ")}: each bound must be a number from 1 to ${MODAL_SIZE_MAX_PX}, and a minimum may not exceed its maximum.`,
			)
		}
		this.post({ type: MSG_MODAL_SIZE, payload: usable })
	}

	/* ---------------------------------------------------------------- */
	/* Public: API                                                      */
	/* ---------------------------------------------------------------- */

	readonly api = {
		/**
		 * A fresh, single-use credential triple. The escape hatch for authors who
		 * want to drive `fetch` themselves — every triple is good for exactly one
		 * public-API call.
		 */
		credentials: (): Promise<ResolvedCredentials> => this.requestCredentials(),

		/**
		 * Call the Livespace public API. Handles the credential round-trip, the
		 * auth params, and one backoff-retry on `conflict`.
		 */
		call: <T = unknown>(
			endpoint: string,
			params: Record<string, ApiParamValue> = {},
		): Promise<T> => this.call<T>(endpoint, params),

		deal: {
			/** `Deal/get` — the typed convenience, unwrapping the `data` envelope. */
			get: async (id: string): Promise<Record<string, unknown>> => {
				const response = await this.call<{ data: Record<string, unknown> }>(
					"Deal/get",
					{ id },
				)
				return (
					response?.data ?? (response as unknown as Record<string, unknown>)
				)
			},
		},
	}

	/* ---------------------------------------------------------------- */
	/* Public: your backend                                             */
	/* ---------------------------------------------------------------- */

	/**
	 * A plugin subject token, for your own backend to verify: it names the
	 * tenant, the viewing user, this installation and this plugin, signed by
	 * Livespace. Send it on every call — `backend.fetch` does.
	 *
	 * Cached, and replaced when fewer than 15 seconds of it remain, so call
	 * this before every request rather than holding on to the result. Calls made
	 * while a request is out share it. `view` mode only.
	 */
	getToken(options: GetTokenOptions = {}): Promise<string> {
		const refused = this.refusalOutsideView("tokens")
		if (refused) {
			return Promise.reject(refused)
		}
		if (this.tokenInFlight) {
			return this.tokenInFlight
		}
		const cached = this.cachedToken
		if (!options.forceRefresh && cached && this.now() < cached.refreshAt) {
			return Promise.resolve(cached.token)
		}

		// Dropped before asking, so a failed refresh leaves nothing stale behind.
		this.cachedToken = null
		const inFlight = this.requestSessionToken().then(({ token, expiresIn }) => {
			const lifetimeMs = expiresIn * 1000
			const margin = Math.min(TOKEN_REFRESH_MARGIN_MS, lifetimeMs / 2)
			// Timed from arrival, not from the request: a slow host must not make
			// the SDK hand out a token closer to expiry than it thinks.
			this.cachedToken = { token, refreshAt: this.now() + lifetimeMs - margin }
			return token
		})
		this.tokenInFlight = inFlight
		const settle = () => {
			if (this.tokenInFlight === inFlight) {
				this.tokenInFlight = null
			}
		}
		inFlight.then(settle, settle)
		return inFlight
	}

	readonly backend = {
		/**
		 * `fetch`, with `Authorization: Bearer <token>` added. On a 401 it gets a
		 * fresh token and tries once more, then hands back whatever came — the
		 * response is yours, error statuses included, exactly as `fetch` returns.
		 *
		 * A retried request is sent again from `init`, so its body must be
		 * re-sendable: a string, `FormData`, `URLSearchParams` or a `Blob`, not a
		 * stream.
		 */
		fetch: (input: RequestInfo | URL, init: RequestInit = {}) =>
			this.backendFetch(input, init),
	}

	private async backendFetch(
		input: RequestInfo | URL,
		init: RequestInit,
	): Promise<Response> {
		const send = async (forceRefresh: boolean) => {
			const token = await this.getToken({ forceRefresh })
			const headers = new Headers(init.headers)
			headers.set("Authorization", `Bearer ${token}`)
			return this.fetchImpl(input, { ...init, headers })
		}
		const response = await send(false)
		if (response.status !== 401) {
			return response
		}
		return send(true)
	}

	private requestSessionToken(): Promise<{ token: string; expiresIn: number }> {
		const requestId = this.randomId()
		const { promise, resolve, reject } = deferred<{
			token: string
			expiresIn: number
		}>()
		this.pendingTokens.set(requestId, { resolve, reject })

		this.post({ type: MSG_REQUEST_SESSION_TOKEN, payload: { requestId } })

		if (this.timeoutMs > 0) {
			setTimeout(() => {
				if (this.pendingTokens.delete(requestId)) {
					reject(
						new LsPluginApiError(
							"timeout",
							"Livespace did not answer the token request in time.",
						),
					)
				}
			}, this.timeoutMs)
		}

		return promise
	}

	private requestCredentials(): Promise<ResolvedCredentials> {
		const refused = this.refusalOutsideView("credentials")
		if (refused) {
			return Promise.reject(refused)
		}
		const requestId = this.randomId()
		const { promise, resolve, reject } = deferred<ResolvedCredentials>()
		this.pendingCredentials.set(requestId, { resolve, reject })

		this.post({
			type: MSG_REQUEST_API_CREDENTIALS,
			payload: { requestId },
		})

		if (this.timeoutMs > 0) {
			setTimeout(() => {
				if (this.pendingCredentials.delete(requestId)) {
					reject(
						new LsPluginApiError(
							"timeout",
							"Livespace did not answer the credentials request in time.",
						),
					)
				}
			}, this.timeoutMs)
		}

		return promise
	}

	private async call<T>(
		endpoint: string,
		params: Record<string, ApiParamValue>,
	): Promise<T> {
		let credentials: ResolvedCredentials
		try {
			credentials = await this.requestCredentials()
		} catch (error) {
			// `conflict` means issuance was throttled or raced — the one error worth
			// retrying. Everything else is a decision the host already made.
			if (error instanceof LsPluginApiError && error.code === "conflict") {
				await this.delay(500)
				credentials = await this.requestCredentials()
			} else {
				throw error
			}
		}

		const query = new URLSearchParams({
			_api_auth: "key",
			_api_key: credentials.apiKey,
			_api_sha: credentials.apiSha,
			_api_session: credentials.apiSession,
		})
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== null) {
				query.set(key, String(value))
			}
		}

		const url = `${credentials.apiBaseUrl}/api/public/json/${endpoint}?${query}`
		let response: Response
		try {
			response = await this.fetchImpl(url)
		} catch (cause) {
			// The request never completed — offline, DNS, CORS. `fetch` rejects with
			// the browser's own TypeError, which has no `code`; wrapping it keeps
			// "every failure has a code" true for plugins that branch on it. Not
			// retried: the credentials were spent on the attempt regardless, and a
			// dropped connection rarely heals in the next 500 ms.
			throw new LsPluginApiError(
				"network_error",
				`Could not reach the Livespace API for ${endpoint}.`,
				undefined,
				{ cause },
			)
		}

		if (!response.ok) {
			throw new LsPluginApiError(
				"http_error",
				`Livespace API responded ${response.status} for ${endpoint}.`,
				response.status,
			)
		}

		try {
			return (await response.json()) as T
		} catch (cause) {
			// A success status with a body that isn't JSON — typically a proxy or
			// gateway page in front of the API. `json()` rejects with a plain
			// SyntaxError; wrapped for the same reason as `network_error`.
			throw new LsPluginApiError(
				"invalid_response",
				`Livespace API returned a response for ${endpoint} that is not JSON.`,
				response.status,
				{ cause },
			)
		}
	}
}
